/**
 * Identity Trust Page (F036 / Trust Engine)
 *
 * Route: /trust | Nav: Model > Identity Trust
 *
 * A lookup screen (search box + single-record view) over the cached-read / compute-on-demand
 * Identity Trust score — NOT a list page, so it deliberately does not use `useAdaptiveGridData` /
 * `ListPageLayout` (see `.github/context/list-grid-standard.context.md` — that standard governs
 * multi-row grids; this is a single-record lookup, closer in shape to a search-then-detail form).
 *
 * Binds to `trustApi` (`api/portal/trust`) — `getProfile` can 404 when the identity doesn't
 * resolve against the governance graph; this is an EXPECTED outcome (EmptyState), not an error.
 *
 * Badge color-map and toast/mutation conventions mirror `McpGovernancePage.tsx` (F032).
 */
import { useState } from 'react';
import type { FC, ReactNode } from 'react';
import {
  Body1,
  Body2,
  Caption1,
  Card,
  Text,
  Badge,
  Button,
  Input,
  Spinner,
  Toaster,
  Toast,
  ToastTitle,
  ToastBody,
  useId,
  useToastController,
  makeStyles,
  tokens,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  ProgressBar,
} from '@fluentui/react-components';
import type { BadgeProps } from '@fluentui/react-components';
import {
  ShieldKeyholeRegular,
  SearchRegular,
  ArrowClockwiseRegular,
  WarningRegular,
} from '@fluentui/react-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { trustApi } from '../services/api';
import type { TrustScore, TrustScoreInput } from '../types';
import { PageHeader } from '../components/common/PageHeader';
import { ErrorState, EmptyState } from '../components/list';
import { usePageStyles } from '../styles/page-styles';

// ---------------------------------------------------------------------------
// Badge color mapping — semantic map, not raw hex (per List & Grid Standard §15 / REVIEW.md).
// ---------------------------------------------------------------------------

const bandColor = (band: string): BadgeProps['color'] => {
  switch (band) {
    case 'High': return 'success';
    case 'Moderate': return 'warning';
    case 'Low':
    case 'Critical': return 'danger';
    case 'Unknown':
    default: return 'subtle';
  }
};

// ---------------------------------------------------------------------------
// Type for the shape `fetchWithAuth` throws on a non-OK response (see `services/api.ts`) — a
// plain `Error` augmented with the HTTP `status`. Narrowed via `instanceof Error` + `in` check
// before use, per the project's `unknown`-at-boundary TypeScript rule.
// ---------------------------------------------------------------------------

interface ApiErrorLike extends Error {
  status?: number;
}

function isApiErrorLike(err: unknown): err is ApiErrorLike {
  return err instanceof Error;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  searchCard: {
    padding: tokens.spacingVerticalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  searchRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: tokens.spacingHorizontalM,
  },
  searchInput: {
    flexGrow: 1,
    minWidth: '20rem',
  },
  resultSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  scoreCard: {
    padding: tokens.spacingVerticalXL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  scoreHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  identityBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  identityId: {
    fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
  },
  badgeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },
  scoreHeadline: {
    display: 'flex',
    alignItems: 'baseline',
    gap: tokens.spacingHorizontalM,
  },
  scoreValue: {
    fontSize: '3rem',
    fontWeight: tokens.fontWeightBold,
    lineHeight: '1',
    color: tokens.colorNeutralForeground1,
  },
  scoreUnit: {
    color: tokens.colorNeutralForeground3,
  },
  progressBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    maxWidth: '28rem',
  },
  metaRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalXXL,
    flexWrap: 'wrap',
  },
  metaField: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  metaLabel: {
    color: tokens.colorNeutralForeground3,
  },
  reasonText: {
    color: tokens.colorNeutralForeground2,
  },
});

// ---------------------------------------------------------------------------
// TrustScorePanel — the score readout + explainability table shown after a successful lookup.
// ---------------------------------------------------------------------------

interface TrustScorePanelProps {
  profile: TrustScore;
  onRecompute: () => void;
  isRecomputing: boolean;
}

const TrustScorePanel: FC<TrustScorePanelProps> = ({ profile, onRecompute, isRecomputing }) => {
  const styles = useStyles();

  return (
    <div className={styles.resultSection}>
      <Card className={styles.scoreCard}>
        <div className={styles.scoreHeader}>
          <div className={styles.identityBlock}>
            <Caption1 className={styles.metaLabel}>Identity</Caption1>
            <Text className={styles.identityId} weight="semibold">{profile.identityExternalId}</Text>
          </div>
          <Button
            appearance="secondary"
            icon={isRecomputing ? <Spinner size="tiny" /> : <ArrowClockwiseRegular />}
            disabled={isRecomputing}
            onClick={onRecompute}
          >
            {isRecomputing ? 'Recomputing…' : 'Recompute'}
          </Button>
        </div>

        <div className={styles.badgeRow}>
          <div className={styles.scoreHeadline}>
            <span className={styles.scoreValue}>{profile.trustScore}</span>
            <span className={styles.scoreUnit}>/ 100</span>
          </div>
          <Badge appearance="tint" size="large" color={bandColor(profile.band)}>{profile.band}</Badge>
        </div>

        <div className={styles.progressBlock}>
          <ProgressBar value={Math.max(0, Math.min(100, profile.trustScore)) / 100} thickness="large" />
        </div>

        <div className={styles.metaRow}>
          <div className={styles.metaField}>
            <Caption1 className={styles.metaLabel}>Confidence</Caption1>
            <Body2>{Math.round(profile.confidence * 100)}%</Body2>
          </div>
          <div className={styles.metaField}>
            <Caption1 className={styles.metaLabel}>Computed At</Caption1>
            <Body2>{new Date(profile.computedAt).toLocaleString()}</Body2>
          </div>
        </div>

        <div className={styles.metaField}>
          <Caption1 className={styles.metaLabel}>Reason</Caption1>
          <Body1 className={styles.reasonText}>{profile.reason}</Body1>
        </div>
      </Card>

      <Card>
        <div style={{ padding: tokens.spacingVerticalL, display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
          <Text weight="semibold">Explainability Breakdown</Text>
          {profile.inputs.length === 0 ? (
            <Caption1>No contributing inputs recorded for this score.</Caption1>
          ) : (
            <Table aria-label="Trust score explainability inputs">
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Name</TableHeaderCell>
                  <TableHeaderCell>Raw Value</TableHeaderCell>
                  <TableHeaderCell>Weight</TableHeaderCell>
                  <TableHeaderCell>Detail</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {profile.inputs.map((input: TrustScoreInput, index: number) => (
                  <TableRow key={`${input.name}-${index}`}>
                    <TableCell>{input.name}</TableCell>
                    <TableCell>{input.rawValue}</TableCell>
                    <TableCell>{input.weight}</TableCell>
                    <TableCell>{input.detail ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </Card>
    </div>
  );
};

// ---------------------------------------------------------------------------
// TrustPage
// ---------------------------------------------------------------------------

export const TrustPage: FC = () => {
  const styles = useStyles();
  const ps = usePageStyles();
  const queryClient = useQueryClient();
  const toasterId = useId('trust-toaster');
  const { dispatchToast } = useToastController(toasterId);

  const [id, setId] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  const {
    data: profile,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery<TrustScore, ApiErrorLike>({
    queryKey: ['trust-profile', id],
    queryFn: () => trustApi.getProfile(id),
    enabled: false,
    retry: false,
  });

  const recomputeMutation = useMutation({
    mutationFn: () => trustApi.recompute(id),
    onSuccess: (updated) => {
      queryClient.setQueryData(['trust-profile', id], updated);
      queryClient.invalidateQueries({ queryKey: ['trust-profile', id] });
      dispatchToast(
        <Toast>
          <ToastTitle>Trust Score Recomputed</ToastTitle>
          <ToastBody>{updated.identityExternalId} is now {updated.trustScore} ({updated.band}).</ToastBody>
        </Toast>,
        { intent: 'success', timeout: 5000 }
      );
    },
    onError: () => {
      dispatchToast(
        <Toast>
          <ToastTitle>Recompute Failed</ToastTitle>
          <ToastBody>Unable to recompute the Trust Score for this identity.</ToastBody>
        </Toast>,
        { intent: 'error', timeout: 5000 }
      );
    },
  });

  const handleLookup = () => {
    if (!id.trim()) return;
    setHasSearched(true);
    void refetch();
  };

  let content: ReactNode;
  if (!hasSearched) {
    content = (
      <EmptyState
        icon={<ShieldKeyholeRegular />}
        title="Look Up an Identity"
        description="Enter an identity external ID above and select Look Up to view its Trust Score and explainability breakdown."
      />
    );
  } else if (isFetching) {
    content = (
      <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXXL }}>
        <Spinner label="Computing trust profile…" />
      </div>
    );
  } else if (isError) {
    const status = isApiErrorLike(error) ? error.status : undefined;
    if (status === 404) {
      content = (
        <EmptyState
          icon={<WarningRegular />}
          title="Identity Not Found"
          description="Identity does not resolve against the governance graph."
        />
      );
    } else {
      content = (
        <ErrorState
          message="Unable to load the Trust Score for this identity."
          onRetry={() => refetch()}
        />
      );
    }
  } else if (profile) {
    content = (
      <TrustScorePanel
        profile={profile}
        onRecompute={() => recomputeMutation.mutate()}
        isRecomputing={recomputeMutation.isPending}
      />
    );
  }

  return (
    <div className={ps.page}>
      <Toaster toasterId={toasterId} position="top-end" />

      <PageHeader
        title="Identity Trust"
        subtitle="The Identity Trust score and explainability breakdown for governed identities"
        icon={<ShieldKeyholeRegular />}
      />

      <Card className={styles.searchCard}>
        <div className={styles.searchRow}>
          <Input
            className={styles.searchInput}
            placeholder="Identity external ID, e.g. agent:..."
            value={id}
            onChange={(_, data) => setId(data.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleLookup();
            }}
          />
          <Button
            appearance="primary"
            icon={<SearchRegular />}
            onClick={handleLookup}
            disabled={isFetching}
          >
            Look Up
          </Button>
        </div>
      </Card>

      {content}
    </div>
  );
};
